/**
 * App-wide settings (single row). The pure default builders live in
 * `./defaults` (no db import) so the Dexie v2 upgrade step and the UI agree
 * on what a complete row looks like; they are re-exported here.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { Settings } from '../types';
import { applySettingsPatch, withSettingsDefaults, type SettingsPatch } from './defaults';

export {
  DEFAULT_AI_SETTINGS,
  DEFAULT_GEMINI_MODEL,
  DEFAULT_ONBOARDING,
  DEFAULT_SETTINGS,
  DEFAULT_UI_SETTINGS,
  applySettingsPatch,
  withSettingsDefaults,
  type SettingsPatch,
} from './defaults';

/** The settings row with defaults applied. Never returns undefined. */
export async function getSettings(db: BookkeepingDB = defaultDb): Promise<Settings> {
  return withSettingsDefaults(await db.settings.get('settings'));
}

/** Merge a patch into the settings row and persist it. */
export async function updateSettings(patch: SettingsPatch, db: BookkeepingDB = defaultDb): Promise<Settings> {
  return db.transaction('rw', db.settings, async () => {
    const next = applySettingsPatch(await getSettings(db), patch);
    await db.settings.put(next);
    return next;
  });
}

export async function setAdvancedMode(advancedMode: boolean, db: BookkeepingDB = defaultDb): Promise<void> {
  await updateSettings({ ui: { advancedMode } }, db);
}

export async function dismissCallout(id: string, db: BookkeepingDB = defaultDb): Promise<void> {
  await db.transaction('rw', db.settings, async () => {
    const current = await getSettings(db);
    if (current.ui.dismissedCallouts.includes(id)) return;
    await db.settings.put(applySettingsPatch(current, { ui: { dismissedCallouts: [...current.ui.dismissedCallouts, id] } }));
  });
}
